import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { LoginService } from 'app/login.service';
import { Article } from 'app/models/Article';
import { Dividende } from 'app/models/Dividende';
import { MarketData } from 'app/models/MarketData';
import { Utilisateur } from 'app/models/Utilisateur';
import { PopupLoginComponent } from 'app/modules/popup-login/popup-login.component';
import { ArticleSchemaService } from 'app/shared/ArticleSchema.service';
import { LinkService } from 'app/shared/Link.service';
import { AbsPipe } from 'app/shared/abs.pipe';
import { ConvertToSlugPipe } from 'app/shared/convert-to-slug.pipe';
import { DataService } from 'app/shared/data.service';
import { ExtractTextFromHtmlPipe } from 'app/shared/extract-text-from-html.pipe';
import { MetaService } from 'app/shared/meta.service';
import { UtilisateurService } from 'app/shared/utilisateur.service';
import { environment } from 'environments/environment';
import { IChartApi, ISeriesApi } from 'lightweight-charts';
import { ActifSearchComponent } from '../../actif-search/actif-search.component';
import { ImageComponent } from '../../image/image.component';
import { StatsCardsComponent } from '../../media/stats-cards/stats-cards.component';
@Component({
    selector: 'app-mur-actif',
    templateUrl: './mur-actif.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ActifSearchComponent,
        StatsCardsComponent,
        MatIcon,
        AbsPipe,
        ConvertToSlugPipe,
        ExtractTextFromHtmlPipe,
        RouterModule,
        ImageComponent,
    ],
})
export class MurActifComponent implements OnInit {
    cloud_front = environment.cloud_front_host;
    @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
    @ViewChild('scrollContainer', { static: true })
    scrollContainer!: ElementRef;
    @ViewChild('tooltipContainer', { static: true })
    tooltipContainer: ElementRef;
    showStartGradient: boolean = false;
    showEndGradient: boolean = false;
    showStats: boolean = false;
    tooltipValue: number;
    showTooltip: boolean = false;
    isLoggedIn?: boolean;
    tooltipDate?: Date;
    actif: MarketData;
    user?: Utilisateur;
    dividendes?: Dividende;
    baisses: MarketData[];
    volumes: MarketData[];
    hausses: MarketData[];
    news: Article[] = [];
    private chart: IChartApi;
    private lineSeries: ISeriesApi<'Area'>;

    constructor(
        @Inject(PLATFORM_ID) private platformId,
        private _changeDetectorRef: ChangeDetectorRef,
        private loginService: LoginService,
        @Inject(DOCUMENT) private doc,
        private route: ActivatedRoute,
        private dataService: DataService,
        private seoService: LinkService,
        private dialog: MatDialog,
        private metaService: MetaService,
        private utilisateurService: UtilisateurService,
        private articleSchemaService: ArticleSchemaService
    ) {
        this.isLoggedIn = this.loginService.getIsLogged();
    }

    ngOnInit(): void {
        this.route.params.subscribe(() => {
            ({
                actif: {
                    data: this.actif,
                    dividendes: this.dividendes,
                    news: this.news,
                },
                principaux: [this.volumes, this.hausses, this.baisses],
            } = this.route.snapshot.data);
            if (isPlatformBrowser(this.platformId)) {
                if (!this.chart)
                    (async () => {
                        const { createChart } = await import(
                            'lightweight-charts'
                        );

                        window.addEventListener('resize', () =>
                            this.checkScroll()
                        );
                        this.checkScroll();
                        this.chart = createChart(
                            this.chartContainer.nativeElement,
                            {
                                height: this.chartContainer.nativeElement
                                    .clientHeight,
                                width: this.chartContainer.nativeElement
                                    .clientWidth,
                                timeScale: {
                                    timeVisible: true,
                                    secondsVisible: false,
                                },
                                localization: {
                                    timeFormatter: (time) => {
                                        const date = new Date(time * 1000); // Convert Lightweight Charts timestamp to milliseconds
                                        return date.toLocaleString('fr', {
                                            timeZone: 'Africa/Tunis',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                        });
                                    },
                                },
                                watermark: {
                                    visible: true,
                                    fontSize: 24,
                                    horzAlign: 'center',
                                    vertAlign: 'center',
                                    color: '#bcbcbc',
                                    text: 'Irbe7',
                                },
                                autoSize: true,
                                layout: {
                                    background: {
                                        color: '#fbfbfb',
                                    },
                                },
                            }
                        );
                        this.lineSeries = this.chart.addAreaSeries({
                            lineColor: '#1b51af', // Line color set to blue
                            topColor: 'rgba(27, 81, 175, 0.5)', // Shadow color at the top, matching the line color with transparency
                            bottomColor: 'rgba(27, 81, 175, 0.1)', // Shadow color at the bottom for a gradient effect
                            lineWidth: 1,
                        });
                        window.document.getElementById(
                            'tv-attr-logo'
                        ).style.display = 'none';
                        this.chart.subscribeCrosshairMove((param) => {
                            if (
                                param.point === undefined ||
                                !param.time ||
                                param.point.x < 0 ||
                                param.point.x >
                                    this.chartContainer.nativeElement
                                        .clientWidth ||
                                param.point.y < 0 ||
                                param.point.y >
                                    this.chartContainer.nativeElement
                                        .clientHeight
                            ) {
                                this.showTooltip = false;
                                this._changeDetectorRef.markForCheck();
                            } else {
                                const data: any = param.seriesData.get(
                                    this.lineSeries
                                );
                                if (data) {
                                    this.showTooltip = true;
                                    this.tooltipDate = new Date(
                                        data.time * 1000
                                    );
                                    this.tooltipValue = data.value.toFixed(2);
                                    this.tooltipContainer.nativeElement.style.left =
                                        param.point.x -
                                        this.tooltipContainer.nativeElement
                                            .offsetWidth /
                                            2 +
                                        'px';
                                    this.tooltipContainer.nativeElement.style.top =
                                        param.point.y -
                                        this.tooltipContainer.nativeElement
                                            .offsetHeight -
                                        5 +
                                        'px';
                                    this._changeDetectorRef.markForCheck();
                                }
                            }
                        });
                    })();
                this.showStats = false;
                this.chargerGraphique();
            }
        });

        if (this.isLoggedIn !== undefined) {
            if (this.isLoggedIn)
                this.utilisateurService
                    .getUtilisateurByUsername(this.loginService.getUsername())
                    .subscribe((users) => {
                        this.user = users[0];
                    });
        }
     let canonicalUrl = this.doc.URL.replace(/^http:/, 'https:');

        this.metaService.updateMetaTags({
            title: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            description: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
            ogTitle: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            ogUrl: canonicalUrl,

            ogDescription: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
            twitterTitle: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            twitterDescription: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
        });

        // Générer les schémas structurés
        this.articleSchemaService.generateGlobalWebPageSchema({
            url: canonicalUrl,
            name: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            description: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
            isPartOf: {
                name: 'Irbe7',
                url: environment.apiUrl,
            },
        });
                this.createLinkForCanonicalURL();
    }
    updateMetaTags(): void {
        let canonicalUrl = this.doc.URL.replace(/^http:/, 'https:');

        this.metaService.updateMetaTags({
            title: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            description: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
            ogTitle: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            ogUrl: canonicalUrl,

            ogDescription: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
            twitterTitle: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            twitterDescription: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
        });

        // Générer les schémas structurés
        this.articleSchemaService.generateGlobalWebPageSchema({
            url: canonicalUrl,
            name: ` ${this.actif.referentiel.stockName} : Cours, Graphiques, Actualités et Analyses boursières | [irbe7.com]`,
            description: `Retrouvez toutes les informations sur l'action ${this.actif.referentiel.stockName} cotation en direct, graphiques interactifs, actualités financières, analyses d'experts et des analystes.`,
            isPartOf: {
                name: 'Irbe7',
                url: environment.apiUrl,
            },
        });
    }
    createLinkForCanonicalURL() {
        this.seoService.createLinkForCanonicalURL();
    }
    chargerGraphique() {
        if (this.lineSeries) this.lineSeries.setData([]);
        this.dataService
            .recupererMiniChart(
                this.actif.referentiel.stockName,
                '1D',
                Math.floor(new Date().getTime() / 1000),
                2
            )
            .subscribe((data) => {
                this.lineSeries.setData(data.data);
            });
    }

    checkScroll() {
        if (this.scrollContainer) {
            const hasScroll =
                this.scrollContainer.nativeElement.scrollWidth >
                this.scrollContainer.nativeElement.clientWidth;

            if (hasScroll) {
                this.showStartGradient =
                    this.scrollContainer.nativeElement.scrollLeft > 0;
                const isAtEnd =
                    this.scrollContainer.nativeElement.scrollLeft +
                        this.scrollContainer.nativeElement.clientWidth >=
                    this.scrollContainer.nativeElement.scrollWidth - 1;
                this.showEndGradient = !isAtEnd;
            } else {
                this.showStartGradient = false;
                this.showEndGradient = false;
            }
        }
    }

    addToWatchlist() {
        if (!this.isLoggedIn) this.dialog.open(PopupLoginComponent);
        else
            this.utilisateurService
                .mettreAJourUtilisateur(this.user.userId, {
                    ...this.user,
                    watchlist: [
                        ...this.user.watchlist,
                        this.actif.referentiel.stockName,
                    ],
                })
                .subscribe((user: Utilisateur) => {
                    this.user = user;
                });
    }

    showAddButton() {
        return (
            this.isLoggedIn !== undefined &&
            (!this.isLoggedIn ||
                (this.user &&
                    !this.user?.watchlist?.includes(
                        this.actif?.referentiel?.stockName
                    )))
        );
    }

    showFollowButton() {
        return (
            this.isLoggedIn !== undefined &&
            this.user &&
            this.user?.watchlist?.includes(this.actif?.referentiel?.stockName)
        );
    }
}
